import type { RefObject } from "react";
import { AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { RestaurantDetail } from "@/components/restaurant";
import { ModalShell } from "@/components/ui/ModalShell";
import type { GeolocationState } from "@/hooks/useGeolocation";
import { useTranslation } from "@/i18n/I18nProvider";
import type { Discount, Restaurant } from "@/types/restaurant";

/**
 * The venue detail dialog every feed surface (cards, strips, map pins) opens:
 * the venue's page. One column with the photo on top, wider than the old
 * dialog (about 768 px, 896 px from `lg`), near full height on phones; the
 * same size for every venue.
 */
export function VenueDetailModal({
  restaurant,
  discounts,
  geo,
  panelRef,
  onClose,
  onLeaveFeedback,
  onOpenBonus,
}: {
  restaurant: Restaurant | null;
  discounts: Discount[] | undefined;
  /** Omitted (with onLeaveFeedback) by the server-free demo: no Dine In check-in. */
  geo?: GeolocationState;
  panelRef: RefObject<HTMLDivElement>;
  onClose: () => void;
  onLeaveFeedback?: () => void;
  onOpenBonus?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {/* `fade` motion — any transform (scale or slide) composites the
          clipped hero image during the animation and flashes a seam at
          its bottom edge. */}
      {restaurant && (
        <ModalShell
          key="floor-plan-backdrop"
          motionStyle="fade"
          zClass="z-[100]"
          onBackdropClick={onClose}
          aria-labelledby="restaurant-detail-title"
          backdropClass="p-2 sm:p-4"
          panelRef={panelRef}
          panelClass="max-h-[94dvh] max-w-3xl overflow-y-auto overscroll-contain sm:max-h-[90vh] lg:max-w-4xl"
        >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-20 grid h-11 w-11 place-items-center rounded-full border border-zinc-800 light:border-black/10 bg-zinc-900/80 light:bg-white/80 text-zinc-300 light:text-zinc-700 transition hover:border-zinc-500 light:hover:border-black/30 hover:text-zinc-50 light:hover:text-zinc-900 sm:right-4 sm:top-4 sm:h-9 sm:w-9"
              aria-label={t("CLOSE")}
            >
              <X className="h-5 w-5 sm:h-4 sm:w-4" aria-hidden />
            </button>

            <RestaurantDetail
              restaurant={restaurant}
              discounts={discounts}
              titleId="restaurant-detail-title"
              geo={geo}
              onLeaveFeedback={onLeaveFeedback}
              onOpenBonus={onOpenBonus}
            />
        </ModalShell>
      )}
    </AnimatePresence>
  );
}
